<template>
    <v-card outlined>
        <v-card-text>

            <!-- Search Autocomplete -->
            <v-autocomplete v-model="selectedPlace"
                            :items="searchResults"
                            :loading="isSearching"
                            :search-input.sync="searchQuery"
                            item-text="display_name"
                            item-value="place_id"
                            :label="$t('SearchLocation') || 'Search location...'"
                            prepend-inner-icon="mdi-magnify"
                            return-object
                            clearable
                            hide-no-data
                            no-filter
                            outlined
                            dense
                            class="mb-2"
                            @change="onPlaceSelected">
                <template v-slot:item="{ item }">
                    <v-list-item-content>
                        <v-list-item-title>{{ item.display_name }}</v-list-item-title>
                    </v-list-item-content>
                </template>
            </v-autocomplete>

            <!-- Map -->
            <div class="map-wrapper">
                <div ref="map" class="map"></div>
            </div>

        </v-card-text>
    </v-card>
</template>

<script>
    import * as maptilerSdk from '@maptiler/sdk'
    import '@maptiler/sdk/dist/maptiler-sdk.css'

    export default {
        name: 'LocationPicker',

        props: {
            value: {
                type: Object,
                default: () => ({ lat: null, lng: null })
            }
        },

        data() {
            return {
                map: null,
                marker: null,

                lat: this.value.lat,
                lng: this.value.lng,

                searchQuery: '',
                searchResults: [],
                selectedPlace: null,
                isSearching: false,
                searchTimeout: null
            }
        },

        watch: {
            value: {
                handler(newVal) {
                    // Ignore null or placeholder (0,0) so we don't jump to ocean before real location loads
                    if (!newVal || newVal.lat == null || newVal.lng == null) return
                    if (newVal.lat === 0 && newVal.lng === 0) return
                    if (this.lat === newVal.lat && this.lng === newVal.lng) return

                    this.lat = newVal.lat
                    this.lng = newVal.lng
                    if (this.map) {
                        this.map.flyTo({
                            center: [newVal.lng, newVal.lat],
                            zoom: 15,
                            speed: 1
                        })
                        this.addMarker(newVal.lng, newVal.lat)
                    }
                },
                deep: true
            },
            searchQuery(val) {
                if (this.searchTimeout) clearTimeout(this.searchTimeout)

                if (!val || val.length < 3) {
                    this.searchResults = []
                    return
                }

                this.searchTimeout = setTimeout(() => {
                    this.searchLocation(val)
                }, 400)
            }
        },

        mounted() {
            this.$nextTick(() => {
                this.initMap()
            })
        },

        beforeDestroy() {
            if (this.searchTimeout) clearTimeout(this.searchTimeout)
            if (this.map) this.map.remove()
        },

        methods: {
            initMap() {
                maptilerSdk.config.apiKey = 'Cf8QNms6ryAVg2U9ALrS'

                this.map = new maptilerSdk.Map({
                    container: this.$refs.map,
                    style: maptilerSdk.MapStyle.STREETS_V2,
                    language: 'ar', // 🔥 FORCED ARABIC
                    center: [
                        this.lng || 42.1625726,
                        this.lat || 25.4147766
                    ],
                    zoom: 7
                })

                // Initial marker
                if (this.lat && this.lng) {
                    this.addMarker(this.lng, this.lat)
                }

                // Map click
                this.map.on('click', e => {
                    this.lat = e.lngLat.lat
                    this.lng = e.lngLat.lng

                    this.addMarker(this.lng, this.lat)

                    this.$emit('input', {
                        lat: this.lat,
                        lng: this.lng
                    })
                })
            },

            addMarker(lng, lat) {
                if (this.marker) {
                    this.marker.setLngLat([lng, lat])
                } else {
                    this.marker = new maptilerSdk.Marker({ draggable: true })
                        .setLngLat([lng, lat])
                        .addTo(this.map)

                    this.marker.on('dragend', () => {
                        const pos = this.marker.getLngLat()
                        this.lat = pos.lat
                        this.lng = pos.lng

                        this.$emit('input', {
                            lat: this.lat,
                            lng: this.lng
                        })
                    })
                }
            },

            async searchLocation(query) {
                this.isSearching = true
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
                        {
                            headers: {
                                'Accept-Language': this.$i18n?.locale || 'ar'
                            }
                        }
                    )
                    this.searchResults = await response.json()
                } catch (e) {
                    console.error(e)
                    this.searchResults = []
                } finally {
                    this.isSearching = false
                }
            },

            onPlaceSelected(place) {
                if (!place) return

                const lat = parseFloat(place.lat)
                const lng = parseFloat(place.lon)

                this.lat = lat
                this.lng = lng

                this.addMarker(lng, lat)

                this.map.flyTo({
                    center: [lng, lat],
                    zoom: 15,
                    speed: 1
                })

                this.$emit('input', { lat, lng })
            }
        }
    }
</script>

<style scoped>
    .map-wrapper {
        position: relative;
        z-index: 0;
    }

    .map {
        height: 400px;
        width: 100%;
        direction: rtl;
    }
</style>

<style>
    .v-menu__content {
        z-index: 9999999 !important;
    }
    .maplibregl-ctrl-bottom-left, .maplibregl-ctrl-bottom-right {
        display:none !important
    }
</style>
